// Frontend binding between a successful dry run and the confirmation state.
//
// These identifiers are references only — never authorization. The apply endpoint
// must independently verify the admin, run ownership, package fingerprint and run
// status before writing anything.

export type DryRunLike = {
  run_id: string | null;
  import_submission_key?: string | null;
  status: "ready" | "failed";
  workbook: { package_fingerprint: string };
};

export type ImportSession = {
  runId: string;
  submissionKey: string;
  packageFingerprint: string;
};

/**
 * Returns the session carried by a successful dry-run response.
 * The key is taken verbatim from the server — a replacement is never generated.
 */
export const sessionFromDryRun = (d: DryRunLike | null | undefined): ImportSession | null => {
  if (!d || d.status !== "ready") return null;
  if (!d.run_id || !d.import_submission_key) return null;
  if (!d.workbook?.package_fingerprint) return null;
  return {
    runId: d.run_id,
    submissionKey: d.import_submission_key,
    packageFingerprint: d.workbook.package_fingerprint,
  };
};

/** Stable description of the current file selection; changes whenever the files change. */
export const selectionToken = (workbook: File | null, images: File[]): string => {
  const part = (f: File) => `${f.name}:${f.size}:${f.lastModified}`;
  return [workbook ? part(workbook) : "-", ...images.map(part)].join("|");
};

/** A dry-run response may only be used if it belongs to the newest request. */
export const isCurrentResponse = (responseSeq: number, latestSeq: number): boolean =>
  responseSeq === latestSeq;

/** The stored session may only be applied against the package it was issued for. */
export const sessionMatchesResult = (
  session: ImportSession | null,
  d: DryRunLike | null,
): boolean =>
  Boolean(session && d && d.status === "ready" &&
    session.packageFingerprint === d.workbook?.package_fingerprint &&
    session.runId === d.run_id);
